import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { studyMissionsConstants } from "@data/constants/studyMissionsConstants"
import { authService } from "@data/services/authService"
import { studyMissionsService } from "@data/services/studyMissionsService"

const initialState = {
    // draftsByOccurrenceAndStudent[occurrenceId][studentId] = draft (full generator output)
    draftsByOccurrenceAndStudent: {},
    // prescriptionsByOccurrenceAndStudent[occurrenceId][studentId] = { activity_ids, activities_count, prescribed_at }
    prescriptionsByOccurrenceAndStudent: {},
    // failedDraftsByOccurrenceAndStudent[occurrenceId][studentId] = true
    //
    // Les generations en echec se notent ici, et non dans l'ecran. Le fan-out
    // ecrit N requetes sur le meme emplacement d'API : deux echecs dans le
    // meme tick sont groupes par React, l'effet ne se rejoue qu'une fois et
    // seul le dernier eleve serait signale — les autres resteraient sur
    // « Je prepare la sequence… » indefiniment. Le reducer, lui, voit chaque
    // action.
    failedDraftsByOccurrenceAndStudent: {},
}

// Seul point d'écriture du slice des brouillons : les trois cas ci-dessous
// répétaient le même spread à trois niveaux, à maintenir en triple.
function writeDraft(state, oid, sid, draft) {
    return {
        ...state,
        draftsByOccurrenceAndStudent: {
            ...state.draftsByOccurrenceAndStudent,
            [oid]: {
                ...(state.draftsByOccurrenceAndStudent[oid] ?? {}),
                [sid]: draft,
            },
        },
    }
}

function writeDraftFailure(state, oid, sid, failed) {
    // L'etat rehydrate par redux-persist vient d'une version qui ne
    // connaissait pas cette cle : sans ce repli, le premier brouillon d'un
    // utilisateur qui met a jour l'appli plante ici, et l'ecran reste vide.
    const parOccurrence = state.failedDraftsByOccurrenceAndStudent ?? {}
    const pourOccurrence = { ...(parOccurrence[oid] ?? {}) }
    if (failed) {
        pourOccurrence[sid] = true
    } else {
        delete pourOccurrence[sid]
    }

    return {
        ...state,
        failedDraftsByOccurrenceAndStudent: {
            ...parOccurrence,
            [oid]: pourOccurrence,
        },
    }
}

function setDraft(state, draft) {
    if (!draft?.live_occurrence_id || !draft?.student_id) return state
    // Un brouillon qui arrive efface l'echec precedent : c'est ce qui fait
    // disparaitre le « Reessayer » quand le nouvel essai aboutit.
    const propre = writeDraftFailure(
        state,
        draft.live_occurrence_id,
        draft.student_id,
        false
    )
    return writeDraft(propre, draft.live_occurrence_id, draft.student_id, draft)
}

function markDraftFailed(state, action) {
    // L'eleve concerne voyage dans les `urlParams` : c'est le seul canal que
    // l'action d'echec transporte (elle ne porte pas le corps de la requete).
    const oid = action?.urlParams?.live_occurrence_id
    const sid = action?.urlParams?.student_id
    if (!oid || !sid) return state
    return writeDraftFailure(state, oid, sid, true)
}

function appendActivity(state, payload) {
    if (!payload?.live_occurrence_id || !payload?.student_id || !payload?.activity) {
        return state
    }
    const oid = payload.live_occurrence_id
    const sid = payload.student_id
    const draft = state.draftsByOccurrenceAndStudent[oid]?.[sid]
    if (!draft) return state

    return writeDraft(state, oid, sid, {
        ...draft,
        activities: [...(draft.activities ?? []), payload.activity],
    })
}

function removeDraftActivity(state, action) {
    const oid = action?.liveOccurrenceId
    const sid = action?.studentId
    const index = action?.index
    if (!oid || !sid || typeof index !== "number") return state
    const draft = state.draftsByOccurrenceAndStudent[oid]?.[sid]
    const activities = draft?.activities
    if (!Array.isArray(activities) || index < 0 || index >= activities.length) {
        return state
    }

    return writeDraft(state, oid, sid, {
        ...draft,
        activities: activities.filter((_, i) => i !== index),
        // Une liste vide n'a pas la même signification selon qu'elle vient du
        // générateur ou du tuteur : l'écran doit pouvoir dire « tu as tout
        // retiré » plutôt que « je n'ai pas pu générer ».
        emptiedByTutor: activities.length === 1,
    })
}

function clearPrescription(state, payload) {
    if (!payload?.live_occurrence_id || !payload?.student_id) return state
    const oid = payload.live_occurrence_id
    const sid = payload.student_id
    const slice = state.prescriptionsByOccurrenceAndStudent[oid]
    if (!slice || !(sid in slice)) return state
    const { [sid]: _removed, ...rest } = slice
    return {
        ...state,
        prescriptionsByOccurrenceAndStudent: {
            ...state.prescriptionsByOccurrenceAndStudent,
            [oid]: rest,
        },
    }
}

function setPrescription(state, payload) {
    if (!payload?.live_occurrence_id || !payload?.student_id) return state
    const oid = payload.live_occurrence_id
    const sid = payload.student_id
    return {
        ...state,
        prescriptionsByOccurrenceAndStudent: {
            ...state.prescriptionsByOccurrenceAndStudent,
            [oid]: {
                ...(state.prescriptionsByOccurrenceAndStudent[oid] ?? {}),
                [sid]: {
                    activity_ids: payload.activity_ids ?? [],
                    activities_count: payload.activities_count ?? 0,
                    prescribed_at: new Date().toISOString(),
                },
            },
        },
    }
}

export function studyMissions(state = initialState, action) {
    switch (action.type) {
        case studyMissionsService.generate.name + apiConstants._SUCCESS:
        case studyMissionsService.regenerate.name + apiConstants._SUCCESS:
            return setDraft(state, action.data)

        case studyMissionsService.generate.name + apiConstants._FAILURE:
        case studyMissionsService.regenerate.name + apiConstants._FAILURE:
            return markDraftFailed(state, action)

        case studyMissionsService.generateAdditional.name + apiConstants._SUCCESS:
            return appendActivity(state, action.data)

        case studyMissionsConstants.REMOVE_DRAFT_ACTIVITY:
            return removeDraftActivity(state, action)

        case studyMissionsService.prescribe.name + apiConstants._SUCCESS:
            return setPrescription(state, action.data)

        case studyMissionsService.unprescribe.name + apiConstants._SUCCESS:
            return clearPrescription(state, action.data)

        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
            return initialState

        default:
            return state
    }
}

export default studyMissions
